import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchAllSubscriptions, 
  deleteSubscription,
  clearError 
} from '../../../store/slices/subscriptionSlice';
import { 
  HiOutlineSearch, 
  HiOutlineTrash, 
  HiOutlineEye, 
  HiOutlineDownload,
  HiOutlineFilter 
} from 'react-icons/hi';
import './SubscriptionsTracker.scss';

const SubscriptionsTracker = () => {
  const dispatch = useDispatch();
  const { allSubscriptions, loading, error, isDeleting } = useSelector(state => state.subscriptions);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    dispatch(fetchAllSubscriptions());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      alert(`Error: ${error}`);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleDelete = async (subscription) => {
    if (window.confirm(`Are you sure you want to delete the subscription for "${subscription.purpose}"?`)) {
      try {
        await dispatch(deleteSubscription({
          subscriptionId: subscription.id,
          invoiceFileUrl: subscription.invoiceFileUrl,
        })).unwrap();
        alert('Subscription deleted successfully!');
      } catch (error) {
        console.error('Error deleting subscription:', error);
      }
    }
  };

  const handleViewDetails = (subscription) => {
    setSelectedSubscription(subscription);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedSubscription(null);
    setShowModal(false);
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Purpose', 'Vendor', 'Amount', 'Currency', 'Expense Date', 'Invoice', 'User', 'Employee ID', 'Created Date'],
      ...filteredSubscriptions.map(sub => [
        sub.purpose,
        sub.vendor,
        sub.amount,
        sub.currency,
        formatDate(sub.expenseDate),
        sub.invoice || '',
        sub.userName,
        sub.empID || '',
        formatDate(sub.createdAt)
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscriptions_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    let date;
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount || 0);
  };

  // Get unique values for filters
  const uniqueCurrencies = [...new Set(allSubscriptions.map(sub => sub.currency))].filter(Boolean);
  const uniqueUsers = [...new Set(allSubscriptions.map(sub => sub.userName))].filter(Boolean);

  // Filter subscriptions
  const filteredSubscriptions = allSubscriptions.filter(subscription => {
    const matchesSearch = !searchTerm || 
      subscription.purpose?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscription.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscription.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscription.invoice?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCurrency = !filterCurrency || subscription.currency === filterCurrency;
    const matchesUser = !filterUser || subscription.userName === filterUser;
    
    return matchesSearch && matchesCurrency && matchesUser;
  });

  // Calculate totals
  const totalsByUser = filteredSubscriptions.reduce((acc, sub) => {
    const key = sub.userName || 'Unknown';
    if (!acc[key]) acc[key] = 0;
    acc[key] += sub.amount || 0;
    return acc;
  }, {});

  const totalsByCurrency = filteredSubscriptions.reduce((acc, sub) => {
    const key = sub.currency || 'USD';
    if (!acc[key]) acc[key] = 0;
    acc[key] += sub.amount || 0;
    return acc;
  }, {});

  return (
    <div className="subscriptions-tracker-container">
      <div className="page-header">
        <div className="header-content">
          <h1>Subscriptions Tracker</h1>
          <p>Track all employee subscription expenses</p>
        </div>
        <button className="export-btn" onClick={exportToCSV}>
          <HiOutlineDownload /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-filter">
          <HiOutlineSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search subscriptions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="dropdown-filters">
          <select
            value={filterCurrency}
            onChange={(e) => setFilterCurrency(e.target.value)}
          >
            <option value="">All Currencies</option>
            {uniqueCurrencies.map(currency => (
              <option key={currency} value={currency}>{currency}</option>
            ))}
          </select>
          
          <select
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
          >
            <option value="">All Users</option>
            {uniqueUsers.map(user => (
              <option key={user} value={user}>{user}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-section">
        <div className="summary-card">
          <h3>Total Subscriptions</h3>
          <div className="summary-value">{filteredSubscriptions.length}</div>
        </div>
        
        <div className="summary-card">
          <h3>Total by Currency</h3>
          <div className="summary-details">
            {Object.entries(totalsByCurrency).map(([currency, total]) => (
              <div key={currency} className="summary-item">
                <span>{currency}:</span> 
                <span>{formatCurrency(total, currency)}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="summary-card">
          <h3>Active Users</h3>
          <div className="summary-value">{Object.keys(totalsByUser).length}</div>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="table-section">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading subscriptions...</p>
          </div>
        ) : filteredSubscriptions.length === 0 ? (
          <div className="empty-state">
            <p>No subscriptions found.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="subscriptions-table">
              <thead>
                <tr>
                  <th>Purpose</th>
                  <th>Vendor</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>User</th>
                  <th>Invoice Ref</th>
                  <th>File</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscriptions.map((subscription) => (
                  <tr key={subscription.id}>
                    <td className="purpose-cell">
                      <strong>{subscription.purpose}</strong>
                    </td>
                    <td>{subscription.vendor}</td>
                    <td className="amount-cell">
                      {formatCurrency(subscription.amount, subscription.currency)}
                    </td>
                    <td>{formatDate(subscription.expenseDate)}</td>
                    <td>
                      <div className="user-info">
                        <span className="user-name">{subscription.userName}</span>
                        {subscription.empID && (
                          <span className="emp-id">({subscription.empID})</span>
                        )}
                      </div>
                    </td>
                    <td>{subscription.invoice || 'N/A'}</td>
                    <td>
                      {subscription.invoiceFileUrl ? (
                        <button
                          className="file-link-btn"
                          onClick={() => window.open(subscription.invoiceFileUrl, '_blank')}
                          title="View invoice file"
                        >
                          {subscription.invoiceFileName || 'View File'}
                        </button>
                      ) : (
                        'No file'
                      )}
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          className="view-btn"
                          onClick={() => handleViewDetails(subscription)}
                          title="View Details"
                        >
                          <HiOutlineEye />
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(subscription)}
                          disabled={isDeleting}
                          title="Delete"
                        >
                          <HiOutlineTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showModal && selectedSubscription && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Subscription Details</h2>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Purpose:</label>
                  <span>{selectedSubscription.purpose}</span>
                </div>
                
                <div className="detail-item">
                  <label>Vendor:</label>
                  <span>{selectedSubscription.vendor}</span>
                </div>
                
                <div className="detail-item">
                  <label>Amount:</label>
                  <span className="amount">
                    {formatCurrency(selectedSubscription.amount, selectedSubscription.currency)}
                  </span>
                </div>
                
                <div className="detail-item">
                  <label>Expense Date:</label>
                  <span>{formatDate(selectedSubscription.expenseDate)}</span>
                </div>
                
                <div className="detail-item">
                  <label>User:</label>
                  <span>{selectedSubscription.userName}</span>
                </div>
                
                <div className="detail-item">
                  <label>Employee ID:</label>
                  <span>{selectedSubscription.empID || 'N/A'}</span>
                </div>
                
                <div className="detail-item">
                  <label>Email:</label>
                  <span>{selectedSubscription.userEmail}</span>
                </div>
                
                <div className="detail-item">
                  <label>Invoice/Reference:</label>
                  <span>{selectedSubscription.invoice || 'N/A'}</span>
                </div>
                
                <div className="detail-item">
                  <label>Created Date:</label>
                  <span>{formatDate(selectedSubscription.createdAt)}</span>
                </div>
                
                {selectedSubscription.invoiceFileUrl && (
                  <div className="detail-item">
                    <label>Invoice File:</label>
                    <span>
                      <button
                        className="file-link-btn"
                        onClick={() => window.open(selectedSubscription.invoiceFileUrl, '_blank')}
                        title="View invoice file"
                      >
                        {selectedSubscription.invoiceFileName || 'View File'}
                      </button>
                    </span>
                  </div>
                )}
                
                {selectedSubscription.notes && (
                  <div className="detail-item full-width">
                    <label>Notes:</label>
                    <span>{selectedSubscription.notes}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsTracker;